<script setup lang="ts">
import { ref, computed, nextTick, type Component } from 'vue'
import JLayout from './JLayout.vue'
import JHeader from '@/components/organisms/JHeader.vue'
import JSidebarAdvanced from '@/components/organisms/JSidebarAdvanced.vue'
import JDynamicTabs from '@/components/organisms/JDynamicTabs.vue'
import type { SidebarMenuItem, MenuClickEvent, MenuPermission } from '@/types/sidebar-menu.types'
import type { DynamicTab } from '@/types/dynamic-tabs.types'

// Router는 optional (Storybook 환경에서는 없을 수 있음)
// 라이브러리 컴포넌트이므로 Router 의존성을 피하기 위해 optional import
// vue-router는 이미 설치되어 있으므로 일반 import 사용
// 런타임에 Router 인스턴스가 있는지만 체크
import { RouterView, useRouter } from 'vue-router'

/**
 * JLayoutAdvanced - Advanced Sidebar + JDynamicTabs 조합 템플릿 (templates)
 * Advanced Layout Template
 * 
 * @description
 * JLayout을 사용하는 Preset 템플릿입니다.
 * JHeader, JSidebarAdvanced, JDynamicTabs, JPageContainer를 조합한 기본 레이아웃 예시를 제공합니다.
 * 
 * 레이아웃 구조:
 * - header: 상단 헤더 영역 (슬롯)
 * - sidebar: 사이드바 영역 (슬롯)
 * - content: 메인 콘텐츠 영역 (슬롯)
 * 
 * ⚠️ 중요: menuItems의 path를 활용하여 RouterView로 자동 탭 생성 및 경로 이동이 가능합니다.
 * 
 * Router가 있는 경우: menuItems의 path를 기반으로 자동 탭 생성되며, 
 * content-tab-{id} 슬롯으로 RouterView를 제공하면 됩니다.
 * 
 * Router가 없는 경우: content-tab-{id} 슬롯으로 직접 컴포넌트를 제공해야 합니다.
 */

const props = withDefaults(defineProps<{
  /** 레이아웃 스타일 타입 */
  styletype?: 'default' | 'minimal'
  /** 콘텐츠 영역 스크롤 가능 여부 */
  contentScroll?: boolean
  /** 추가 CSS 클래스 */
  class?: string
  /** 메뉴 아이템 목록 (자동 탭 생성을 위해 필요) */
  menuItems?: SidebarMenuItem[]
  /** 즐겨찾기 메뉴 키 목록 */
  favorites?: (number | string)[]
  /** 권한 목록 */
  permissions?: MenuPermission[]
  /** 탭 콘텐츠 렌더 방식 ('router-view': 기존 RouterView fallback, 'slot': 외부 slot + resolveComponent 전용) */
  contentMode?: 'router-view' | 'slot'
  /** 메뉴 클릭 시 router.push로 URL 동기화 여부 */
  syncUrl?: boolean
  /** 경로 → 컴포넌트 resolver. Promise 허용. undefined 반환 시 JTabs fallback으로 이어짐 */
  resolveComponent?: (path: string) => Component | Promise<Component> | undefined
}>(), {
  styletype: 'minimal',
  contentScroll: true,
  menuItems: () => [],
  favorites: () => [],
  permissions: () => [],
  contentMode: 'router-view',
  syncUrl: true,
})

const emit = defineEmits<{
  /** 메뉴 클릭 이벤트 (자동 처리되지 않는 경우) */
  menuClick: [event: MenuClickEvent]
  /** 탭 추가 이벤트 */
  tabAdd: [tab: DynamicTab]
  /** 탭 변경 이벤트 */
  tabChange: [tabId: string]
  /** 탭 닫기 이벤트 */
  tabClose: [tabId: string]
  /** 즐겨찾기 변경 이벤트 */
  favoriteChange: [menuKey: number | string | undefined, isFavorite: boolean]
}>()

/**
 * 사이드바 열림 상태
 */
const isSidebarOpen = ref(true)

/**
 * 사이드바 토글 핸들러
 */
const handleSidebarToggle = () => {
  isSidebarOpen.value = !isSidebarOpen.value
}

/**
 * JDynamicTabs 컴포넌트 참조
 */
const tabsRef = ref<InstanceType<typeof JDynamicTabs> | null>(null)

/**
 * 탭 목록 (내부 상태)
 */
const tabs = ref<DynamicTab[]>([])

/**
 * 현재 활성 탭 ID
 */
const activeTabId = ref<string | undefined>(undefined)

/**
 * path를 기반으로 탭 ID 생성
 * 예: /product/info -> tab-product-info
 */
const generateTabIdFromPath = (path: string): string => {
  if (!path) return ''
  // 앞뒤 슬래시 제거 및 특수문자 변환
  const cleanPath = path.replace(/^\/+|\/+$/g, '').replace(/\//g, '-')
  return `tab-${cleanPath}`
}

/**
 * Router 인스턴스 (RouterView 사용을 위해, 선택적)
 * Router가 없을 수 있으므로 optional하게 처리
 */
let router: ReturnType<typeof useRouter> | null = null
try {
  router = useRouter()
} catch {
  // Router가 설정되지 않은 경우 (스토리북 환경 등)
  router = null
}

/**
 * 메뉴 클릭 핸들러 - path 기반 탭 자동 생성
 */
const handleMenuClick = async (event: MenuClickEvent) => {
  const menuItem = event.menuItem

  // Link 타입 메뉴만 탭으로 생성
  if (!menuItem || menuItem.menuType !== 'L') {
    // 자동 처리하지 않는 경우 이벤트 emit
    emit('menuClick', event)
    return
  }

  // path가 없으면 자동 처리 불가
  if (!menuItem.path) {
    emit('menuClick', event)
    return
  }

  // 탭 ID 생성 (path 기반)
  const tabId = generateTabIdFromPath(menuItem.path)

  // 컴포넌트가 마운트될 때까지 대기
  await nextTick()
  await nextTick()

  // 이미 열린 탭이면 활성화하고 종료 (resolveComponent 호출 불필요)
  if (tabsRef.value?.findTab(tabId)) {
    tabsRef.value.activateTab(tabId)
    return
  }

  // resolveComponent가 제공된 경우 탭 컴포넌트 resolve
  let resolvedComponent: Component | undefined
  if (props.resolveComponent && menuItem.path) {
    resolvedComponent = await props.resolveComponent(menuItem.path)
  }

  // JDynamicTabs의 addTab 메서드를 직접 호출하여 탭 추가
  if (tabsRef.value && typeof tabsRef.value.addTab === 'function') {
    try {
      tabsRef.value.addTab({
        id: tabId,
        label: menuItem.label || '제목 없음',
        icon: menuItem.icon,
        closable: true,
        component: resolvedComponent,
        meta: {
          path: menuItem.path,
          menuKey: menuItem.menuKey,
        },
      })
      emit('tabAdd', { id: tabId, label: menuItem.label || '제목 없음', icon: menuItem.icon, closable: true })

      // Router가 있으면 경로로 이동 (Router에 이미 정의된 경로 사용)
      if (router && props.syncUrl) {
        router.push({ path: menuItem.path })
      }
      return
    } catch (error) {
      console.error('탭 추가 실패:', error)
    }
  }

  // tabsRef가 없거나 addTab 실패 시 tabs.value에 직접 추가
  const newTab: DynamicTab = {
    id: tabId,
    label: menuItem.label || '제목 없음',
    icon: menuItem.icon,
    closable: true,
    ...(resolvedComponent ? { component: resolvedComponent } : {}),
    meta: {
      path: menuItem.path,
      menuKey: menuItem.menuKey,
    },
  }

  if (!Array.isArray(tabs.value)) {
    tabs.value = []
  }

  // 이미 있는 탭인지 확인
  const existingTab = tabs.value.find(t => t.id === tabId)
  if (!existingTab) {
    tabs.value = [...tabs.value, newTab]
    activeTabId.value = tabId
    emit('tabAdd', newTab)
  } else {
    activeTabId.value = tabId
  }
  
  // Router가 있으면 경로로 이동
  if (router && props.syncUrl) {
    router.push({ path: menuItem.path })
  }
}

/**
 * 탭 추가 핸들러
 */
const handleTabAdd = (tab: DynamicTab) => {
  // 탭이 추가되면 tabs.value 동기화
  if (!Array.isArray(tabs.value)) {
    tabs.value = []
  }
  const exists = tabs.value.find(t => t.id === tab.id)
  if (!exists) {
    tabs.value.push(tab)
  }
  activeTabId.value = tab.id
  emit('tabAdd', tab)
}

/**
 * 탭 닫기 핸들러
 */
const handleTabClose = (id: string) => {
  if (!tabs.value || !Array.isArray(tabs.value) || tabs.value.length === 0) return
  tabs.value = tabs.value.filter((t: DynamicTab) => t.id !== id)
  if (activeTabId.value === id && Array.isArray(tabs.value) && tabs.value.length > 0 && tabs.value[0]) {
    activeTabId.value = tabs.value[0].id
  } else if (Array.isArray(tabs.value) && tabs.value.length === 0) {
    activeTabId.value = undefined
  }
  emit('tabClose', id)
}

/**
 * 탭 변경 핸들러
 */
const handleTabChange = (id: string) => {
  activeTabId.value = id
  emit('tabChange', id)
}

/**
 * tabs를 computed로 만들어 반응성 확보
 */
const tabsComputed = computed(() => Array.isArray(tabs.value) ? tabs.value : [])
</script>

<template>
  <JLayout v-bind="props">
    <template #header>
      <slot name="header" :is-sidebar-open="isSidebarOpen" :on-sidebar-toggle="handleSidebarToggle">
        <!-- 기본 헤더 예시 -->
        <JHeader 
          logo-text="JWMS Portal" 
          :styletype="props.styletype"
          :show-sidebar-toggle="true"
          :is-sidebar-open="isSidebarOpen"
          @sidebar-toggle="handleSidebarToggle"
        />
      </slot>
    </template>
    <template #sidebar>
      <slot name="sidebar" :is-sidebar-open="isSidebarOpen">
        <!-- 기본 사이드바 예시 -->
        <JSidebarAdvanced 
          :menu-items="props.menuItems" 
          :favorites="props.favorites"
          :permissions="props.permissions"
          :styletype="props.styletype" 
          :is-visible="isSidebarOpen"
          @menu-click="handleMenuClick"
          @favorite-change="(menuKey, isFavorite) => emit('favoriteChange', menuKey, isFavorite)"
        />
      </slot>
    </template>
    <template #content>
      <slot name="content" :tabs="tabsComputed" :active-tab-id="activeTabId" :tabs-ref="tabsRef">
        <!-- 기본 콘텐츠 예시 - JDynamicTabs 포함 -->
        <JDynamicTabs
          ref="tabsRef"
          :initial-tabs="tabsComputed"
          :default-active-id="activeTabId"
          :styletype="props.styletype || 'minimal'"
          :keep-alive="true"
          @tab-add="handleTabAdd"
          @tab-close="handleTabClose"
          @tab-change="handleTabChange"
        >
          <!-- 슬롯을 통해 외부에서 탭 컨텐츠 제공 가능 -->
          <!-- RouterView를 사용하여 Router의 routes를 재사용 (Route 통일성 보장) -->
          <template
            v-for="tab in tabsComputed"
            :key="tab.id"
            #[`content-${tab.id}`]="slotProps"
          >
            <slot :name="`content-${tab.id}`" v-bind="slotProps">
              <template v-if="props.contentMode === 'router-view'">
                <RouterView
                  v-if="tab.meta?.path && router"
                  :key="tab.id"
                />
                <div v-else class="flex-1 p-6">
                  <p class="text-muted-foreground">콘텐츠를 배치해주세요. (Path: {{ tab.meta?.path || 'N/A' }})</p>
                  <p v-if="!router" class="text-xs text-gray-400 mt-2">
                    Router가 없어서 슬롯으로 콘텐츠를 제공해야 합니다.
                  </p>
                </div>
              </template>
              <!-- contentMode='slot': slot을 비움 → JTabs가 tab.component(resolveComponent 결과) 렌더 -->
            </slot>
          </template>
        </JDynamicTabs>
      </slot>
    </template>
  </JLayout>
</template>

